import { useState } from 'react';
import { useToast } from './useToastContext';

interface UseLogDeleteOptions {
  /** Lowercase entity name, e.g. "fall log" — used in toasts and console logs. */
  entityLabel: string;
  /**
   * Performs the delete request. The hey-api client resolves HTTP failures
   * into `{ error }` rather than throwing, so the result is inspected here.
   */
  performDelete: () => Promise<{ error?: unknown }>;
  /** Called once after a successful delete (e.g. reset the form + notify parent). */
  onDeleted: () => void;
}

/**
 * Shared delete flow for patient event-log dialogs (fall/infection): tracks the
 * confirmation-dialog and in-flight state, runs the delete, and surfaces a
 * success/error toast. Keeps the two dialogs from drifting apart.
 */
export function useLogDelete({
  entityLabel,
  performDelete,
  onDeleted,
}: UseLogDeleteOptions): {
  deleting: boolean;
  confirmDeleteOpen: boolean;
  setConfirmDeleteOpen: (open: boolean) => void;
  handleDelete: () => Promise<void>;
} {
  const { success: showSuccess, error: showError } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await performDelete();
      if (res.error) {
        console.error(`Failed to delete ${entityLabel}:`, res.error);
        showError('Error', `Failed to delete ${entityLabel}.`);
        return;
      }
      const titleCased =
        entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
      showSuccess('Deleted', `${titleCased} deleted.`);
      onDeleted();
    } catch (err) {
      console.error(err);
      showError('Error', `Failed to delete ${entityLabel}.`);
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  return { deleting, confirmDeleteOpen, setConfirmDeleteOpen, handleDelete };
}
